import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import qs from 'query-string';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { setEnhancedModalOpen } from '../../actions/EnhancedModalActions';
import { openDeleteModal } from '../../actions/MasterSetupPageActions';
import { UNSAVED_CONFIRM_MESSAGE } from '../../constants/Common';
import * as Urls from '../../constants/Urls';
import { useFormSelector } from '../../custom-hooks';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { VALIDATION_DELAY } from '../../middlewares/formValidator';
import { getUITA, isTrue } from '../../utils/commonUtils';
import { cancelWorkflowStep, submitForm } from '../actions/runtime.actions';
import ControlChangesIcon from '../components/ControlChangesIcon';
import {
  BUTTON_TYPES,
  FORM_KEYS,
  FORM_MODEL_ATTRIBUTES,
  SS_CONTROL_TYPE,
} from '../constants/form-manager.constants';
import { FCM_BUTTON_STYLES } from '../constants/runtime.constants';
import { getFormData, getUnsynchronizedEntityId } from '../selectors/common.selectors';
import { getEvaluatedExpression, getPropByModel } from '../selectors/form-manager.selectors';
import {
  formHasUnsavedChanges,
  getIsExistingRecord,
  isButtonDisabled,
  isMasterSetupEntity,
} from '../selectors/runtime.selectors';
import { getButtonColor, getButtonLabelToDisplay } from '../utils/controls/ssButton.utils';

const useStyles = makeStyles(FCM_BUTTON_STYLES);

function SSButton({
  label = '',
  buttonType,
  onClick,
  commandName,
  commandArgument,
  needSignature,
  isEditor,
  formKey: storeKey,
  causesValidation,
  id,
  index,
  isControlHiddenFCM,
  isControlAddedFCM,
  isControlChangedFCM,
}) {
  const dispatch = useDispatch();
  const classes = useStyles({ isControlAddedFCM });
  const { formInfo = {} } = useFormSelector(getFormData);
  const isMasterSetup = useFormSelector(isMasterSetupEntity);
  const isExistingRecord = useFormSelector(getIsExistingRecord);
  const hasUnsavedChanges = useFormSelector(formHasUnsavedChanges);
  const isDisabled = useFormSelector(isButtonDisabled, {
    buttonType,
    controlType: SS_CONTROL_TYPE.BUTTON,
  });
  const meaning = useFormSelector(getEvaluatedExpression, {
    id,
    index,
    prop: FORM_MODEL_ATTRIBUTES.MEANING,
  });
  const propLabel = useFormSelector(getPropByModel, {
    id,
    prop: FORM_MODEL_ATTRIBUTES.LABEL,
  });
  const operationDelayMessage = useFormSelector(getEvaluatedExpression, {
    id,
    index,
    prop: FORM_MODEL_ATTRIBUTES.OPERATION_DELAY_MESSAGE,
  });
  const { entityName, searchName } = useSelector((state) => state.masterSetupPage);
  const unsynchronizedEntityId = useSelector(getUnsynchronizedEntityId);

  const history = useHistory();
  const location = useLocation();
  const translateString = useTranslation();

  const translatedMeaning = translateString(meaning);

  const unsavedChangesConfirmation = () => {
    return !hasUnsavedChanges || window.confirm(translateString(UNSAVED_CONFIRM_MESSAGE));
  };

  const submitFormByType = (formSubmitType) => {
    const submitDelay = process.env.NODE_ENV === 'test' ? 0 : 200;
    const timeout = VALIDATION_DELAY + submitDelay;
    const signatureNotNeeded = [
      BUTTON_TYPES.OTHER,
      BUTTON_TYPES.SAVE_OFFLINE,
      BUTTON_TYPES.DELETE,
    ].includes(buttonType);
    const useCustomOperationDelayMessage = [
      BUTTON_TYPES.SAVE_AND_SIGNOFF,
      BUTTON_TYPES.SAVE_WITHOUT_SIGNOFF,
      BUTTON_TYPES.SAVE_PUBLIC_DRAFT,
      BUTTON_TYPES.OTHER,
    ].includes(buttonType);

    const causesValidationNotNeeded = buttonType === BUTTON_TYPES.DELETE;
    // wait for form validation
    setTimeout(() => {
      dispatch(
        submitForm({
          formSubmitType,
          commandName,
          commandArgument,
          needSignature: signatureNotNeeded ? false : isTrue(needSignature),
          formKey: storeKey,
          causesValidation: causesValidationNotNeeded ? false : isTrue(causesValidation),
          meaning: signatureNotNeeded ? undefined : translatedMeaning,
          pathname: location.pathname,
          operationDelayMessage: useCustomOperationDelayMessage ? operationDelayMessage : '',
        })
      );
    }, timeout);
  };

  const onClickHandler = () => {
    switch (buttonType) {
      case BUTTON_TYPES.SAVE_OFFLINE:
        submitFormByType('save-offline');
        break;

      case BUTTON_TYPES.OTHER:
      case BUTTON_TYPES.SAVE_WITHOUT_SIGNOFF: // Save (no SignOff)
        submitFormByType('save');
        break;

      case BUTTON_TYPES.SAVE_AND_SIGNOFF: // Save AND SignOff
        submitFormByType('sign-off');
        break;

      case BUTTON_TYPES.SAVE_PUBLIC_DRAFT:
        submitFormByType('save-public-draft');
        break;

      case BUTTON_TYPES.CANCEL:
        if (isMasterSetup) {
          const { entityType: entity, searchName: search } = qs.parse(location.search);
          history.push(
            `${Urls.MASTER_SETUP_PAGE}?entityName=${entityName ?? entity}&searchName=${searchName ??
              search ??
              ''}`
          );
        } else if (isExistingRecord) {
          const { entityType, formKey, itmId } = qs.parse(location.search);

          if (entityType && formKey && itmId) {
            unsavedChangesConfirmation() && dispatch(cancelWorkflowStep(storeKey));
          } else {
            history.push(Urls.HOME);
          }
        } else if (storeKey === FORM_KEYS.MODAL) {
          dispatch(setEnhancedModalOpen(false));
        } else {
          history.push(Urls.HOME);
        }
        break;

      case BUTTON_TYPES.DELETE:
        if (isMasterSetup) {
          dispatch(openDeleteModal());
        }
        break;

      default:
        if (onClick) {
          onClick();
          break;
        }
        // eslint-disable-next-line no-console
        console.log(`No action is defined/performed for buttonType ${buttonType}`);
    }
  };

  return isMasterSetup && !isExistingRecord && commandName === 'Delete' && !isEditor ? null : (
    <span
      className={isControlAddedFCM || isControlChangedFCM ? classes.changesModeWrapper : null}
      ui-ta={getUITA('FormCtrl', 'SSButton', buttonType)}
      data-ss-id={id}
    >
      <Button
        {...getButtonColor(buttonType)}
        style={{ minWidth: '128px' }}
        onClick={onClickHandler}
        disabled={isEditor || isDisabled || isControlHiddenFCM}
        aria-label={buttonType}
      >
        {getButtonLabelToDisplay({
          translateString,
          formInfo,
          unsynchronizedEntityId,
          buttonType,
          propLabel,
          label,
        })}
      </Button>
      {isControlChangedFCM && (
        <span className={classes.changesIcon}>
          <ControlChangesIcon id={id} />
        </span>
      )}
    </span>
  );
}

export default SSButton;
